// Lightweight toast store, modeled on the shadcn-ui pattern.
// One global store rather than React Context so non-component code (e.g. API
// error handlers) can call ``toast()`` without prop drilling a hook in.
import { useEffect, useState } from "react";

import { suggestFaqForError } from "@/lib/faqHints";

type ToastVariant = "default" | "success" | "destructive";

interface ToastInput {
  title?: string;
  description?: string;
  variant?: ToastVariant;
  durationMs?: number;
}

export interface ToastEntry extends Required<Pick<ToastInput, "title" | "description" | "variant">> {
  id: string;
  durationMs: number;
}

type Listener = (toasts: ToastEntry[]) => void;

const listeners = new Set<Listener>();
let toasts: ToastEntry[] = [];

function emit() {
  for (const listener of listeners) {
    listener(toasts);
  }
}

export function toast(input: ToastInput): string {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const entry: ToastEntry = {
    id,
    title: input.title ?? "",
    description: input.description ?? "",
    variant: input.variant ?? "default",
    durationMs: input.durationMs ?? 5000,
  };
  toasts = [...toasts, entry];
  emit();
  // Errors that have a matching FAQ entry make the Help button bounce and preselect it.
  if (entry.variant === "destructive") suggestFaqForError(`${entry.title} ${entry.description}`);
  return id;
}

export function dismissToast(id: string): void {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

export function useToasts(): ToastEntry[] {
  const [state, setState] = useState<ToastEntry[]>(toasts);
  useEffect(() => {
    listeners.add(setState);
    return () => {
      listeners.delete(setState);
    };
  }, []);
  return state;
}
